import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Plus, Search, Pencil, Trash2, Phone, Mail, Globe, MapPin, FileText, Printer, Droplets, Building2 } from "lucide-react";
import { CompanyDialog } from "../printers/CompanyDialog";
import { PrinterDocuments } from "../printers/PrinterDocuments";
import { COMPANY_TYPES, Pill } from "../printers/constants";

const TYPE_COLORS = { supplier: "bg-blue-100 text-blue-700", maintenance: "bg-orange-100 text-orange-700", both: "bg-teal-100 text-teal-700" };

export default function PrinterCompaniesView({ onChanged, refreshKey }) {
    const { hasPermission } = useApp();
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [dialog, setDialog] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (typeFilter) params.type = typeFilter;
            setCompanies(await api.getPrinterCompanies(params));
        } catch { toast.error("Erreur de chargement"); }
    }, [search, typeFilter]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const remove = async (c) => {
        if (!window.confirm(`Supprimer l'entreprise "${c.name}" ?`)) return;
        try { await api.deletePrinterCompany(c.id); toast.success("Entreprise supprimée"); load(); onChanged(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    return (
        <div data-testid="companies-view">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, contact..." className="pl-9" data-testid="company-search" />
                </div>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="company-type-filter">
                    <option value="">Tous types</option>
                    <option value="supplier">Fournisseurs</option>
                    <option value="maintenance">Maintenance / SAV</option>
                </select>
                {hasPermission("printers:manage") && <Button onClick={() => setDialog({ company: null })} className="ml-auto" data-testid="create-company-btn"><Plus className="w-4 h-4 mr-1" />Nouvelle entreprise</Button>}
            </div>

            {companies.length === 0 ? (
                <div className="card"><div className="card-body text-center py-10 text-gray-400 text-sm"><Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucune entreprise. Ajoutez vos fournisseurs de consommables et prestataires de maintenance.</div></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {companies.map((c) => (
                        <div key={c.id} className="card" data-testid={`company-card-${c.id}`}>
                            <div className="card-body p-4">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="min-w-0">
                                        <h3 className="font-semibold text-gray-900 truncate">{c.name}</h3>
                                        <Pill className={TYPE_COLORS[c.type] || TYPE_COLORS.supplier}>{COMPANY_TYPES[c.type] || c.type}</Pill>
                                    </div>
                                    <div className="flex items-center flex-shrink-0">
                                        <Button variant="ghost" size="sm" onClick={() => setDialog({ company: c })} data-testid={`company-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                        {hasPermission("admin") && <Button variant="ghost" size="sm" onClick={() => remove(c)} data-testid={`company-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>}
                                    </div>
                                </div>
                                <div className="space-y-1 text-xs text-gray-600">
                                    {c.contact_name && <p className="font-medium text-gray-700">{c.contact_name}</p>}
                                    {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-teal-600"><Phone className="w-3 h-3" />{c.phone}</a>}
                                    {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-teal-600 truncate"><Mail className="w-3 h-3" />{c.email}</a>}
                                    {c.website && <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-teal-600 truncate"><Globe className="w-3 h-3" />{c.website}</a>}
                                    {c.address && <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{c.address}</p>}
                                    {c.contract_number && <p className="flex items-center gap-1.5 font-mono"><FileText className="w-3 h-3" />{c.contract_number}</p>}
                                    {c.notes && <p className="text-[11px] text-gray-500 italic pt-1 border-t border-gray-50 mt-1">{c.notes}</p>}
                                </div>
                                <div className="mt-3 pt-2 border-t border-gray-50">
                                    <PrinterDocuments documents={c.documents || []} onAdd={(d) => api.addPrinterCompanyDocument(c.id, d)} onChanged={() => { load(); onChanged(); }} compact testId={`company-docs-${c.id}`} />
                                </div>
                                <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-50 text-[10px] text-gray-400">
                                    <span className="inline-flex items-center gap-1"><Printer className="w-3 h-3" />{c.printers_count} imprimante(s)</span>
                                    <span className="inline-flex items-center gap-1"><Droplets className="w-3 h-3" />{c.consumables_count} consommable(s)</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <CompanyDialog company={dialog?.company || null} open={!!dialog} onClose={() => setDialog(null)} onDone={() => { load(); onChanged(); }} />
        </div>
    );
}
